import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { 
  Mic, Square, Play, Pause, Heart, Share2, 
  Twitter, Youtube, Instagram, Volume2, ChevronDown,
  Send, User, Trophy, Clock, Flame, Star, Crown, Zap, Download,
  MessageCircle, ChevronUp
} from "lucide-react";
import TermsOfService from "./pages/TermsOfService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL from user's uploaded image
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aid-center-17/artifacts/xzfmjjft_The%20Fan%20Rant%20Line.png";

// Function to generate shareable image from rant
const generateRantImage = async (rant) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Instagram-friendly square dimensions
  canvas.width = 1080;
  canvas.height = 1080;
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#050505');
  gradient.addColorStop(1, '#0a1628');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Add halftone pattern effect
  ctx.fillStyle = 'rgba(0, 85, 255, 0.03)';
  for (let x = 0; x < canvas.width; x += 20) {
    for (let y = 0; y < canvas.height; y += 20) {
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  
  // Blue accent bar at top
  ctx.fillStyle = '#0055FF';
  ctx.fillRect(0, 0, canvas.width, 8);
  
  // Load and draw logo
  try {
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      logo.onload = resolve;
      logo.onerror = reject;
      logo.src = LOGO_URL;
    });
    const logoSize = 180;
    ctx.drawImage(logo, (canvas.width - logoSize) / 2, 50, logoSize, logoSize);
  } catch (e) {
    // If logo fails, draw text instead
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 36px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('THE FAN RANT LINE', canvas.width / 2, 140);
  }
  
  // "FAN RANT" header
  ctx.fillStyle = '#0055FF';
  ctx.font = 'bold 42px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('⚡ FAN RANT ⚡', canvas.width / 2, 280);
  
  // Username
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 48px Arial';
  ctx.fillText(rant.username.toUpperCase(), canvas.width / 2, 360);
  
  // Team
  ctx.fillStyle = '#0055FF';
  ctx.font = 'bold 32px Arial';
  ctx.fillText(`vs ${rant.team}`, canvas.width / 2, 410);
  
  // Quote box background
  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 3;
  const boxX = 60;
  const boxY = 460;
  const boxW = canvas.width - 120;
  const boxH = 400;
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeRect(boxX, boxY, boxW, boxH);
  
  // Blue accent on quote box
  ctx.fillStyle = '#0055FF';
  ctx.fillRect(boxX, boxY, 6, boxH);
  
  // Rant content with word wrap
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '32px Arial';
  ctx.textAlign = 'left';
  
  const rantText = rant.rant_type === 'text' ? rant.content : '🎙️ [Voice Rant - Listen on thefanrantline.com]';
  const words = rantText.split(' ');
  let line = '"';
  let y = boxY + 60;
  const maxWidth = boxW - 60;
  const lineHeight = 44;
  
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, boxX + 30, y);
      line = words[i] + ' ';
      y += lineHeight;
      if (y > boxY + boxH - 60) {
        ctx.fillText(line + '..."', boxX + 30, y);
        break;
      }
    } else {
      line = testLine;
    }
    if (i === words.length - 1) {
      ctx.fillText(line + '"', boxX + 30, y);
    }
  }
  
  // Footer with website
  ctx.fillStyle = '#6B7280';
  ctx.font = '28px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('thefanrantline.com', canvas.width / 2, 940);
  
  // Tagline
  ctx.fillStyle = '#0055FF';
  ctx.font = 'bold 24px Arial';
  ctx.fillText('Your Voice in Sports Commentary', canvas.width / 2, 980);
  
  // Blue accent bar at bottom
  ctx.fillStyle = '#0055FF';
  ctx.fillRect(0, canvas.height - 8, canvas.width, 8);
  
  // Convert to blob and download
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fanrant_${rant.username.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 'image/png');
};

// Sports categories
const SPORTS_CATEGORIES = [
  { id: "all", name: "ALL SPORTS", icon: "🏆" },
  { id: "nfl", name: "NFL", icon: "🏈" },
  { id: "nba", name: "NBA", icon: "🏀" },
  { id: "mlb", name: "MLB", icon: "⚾" },
  { id: "nhl", name: "NHL", icon: "🏒" },
  { id: "mls", name: "MLS", icon: "⚽" },
  { id: "soccer", name: "SOCCER", icon: "⚽" },
  { id: "nascar", name: "NASCAR", icon: "🏎️" },
  { id: "golf", name: "GOLF", icon: "⛳" },
  { id: "college", name: "COLLEGE", icon: "🎓" },
  { id: "other", name: "OTHER", icon: "🎯" }
];

// Sports teams for dropdown organized by category
const SPORTS_TEAMS = {
  nfl: [
    "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills",
    "Carolina Panthers", "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns",
    "Dallas Cowboys", "Denver Broncos", "Detroit Lions", "Green Bay Packers",
    "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars", "Kansas City Chiefs",
    "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
    "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants",
    "New York Jets", "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers",
    "Seattle Seahawks", "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders"
  ],
  nba: [
    "Atlanta Hawks", "Boston Celtics", "Brooklyn Nets", "Charlotte Hornets",
    "Chicago Bulls", "Cleveland Cavaliers", "Dallas Mavericks", "Denver Nuggets",
    "Detroit Pistons", "Golden State Warriors", "Houston Rockets", "Indiana Pacers",
    "LA Clippers", "Los Angeles Lakers", "Memphis Grizzlies", "Miami Heat",
    "Milwaukee Bucks", "Minnesota Timberwolves", "New Orleans Pelicans", "New York Knicks",
    "Oklahoma City Thunder", "Orlando Magic", "Philadelphia 76ers", "Phoenix Suns",
    "Portland Trail Blazers", "Sacramento Kings", "San Antonio Spurs", "Toronto Raptors",
    "Utah Jazz", "Washington Wizards"
  ],
  mlb: [
    "Arizona Diamondbacks", "Atlanta Braves", "Baltimore Orioles", "Boston Red Sox",
    "Chicago Cubs", "Chicago White Sox", "Cincinnati Reds", "Cleveland Guardians",
    "Colorado Rockies", "Detroit Tigers", "Houston Astros", "Kansas City Royals",
    "Los Angeles Angels", "Los Angeles Dodgers", "Miami Marlins", "Milwaukee Brewers",
    "Minnesota Twins", "New York Mets", "New York Yankees", "Oakland Athletics",
    "Philadelphia Phillies", "Pittsburgh Pirates", "San Diego Padres", "San Francisco Giants",
    "Seattle Mariners", "St. Louis Cardinals", "Tampa Bay Rays", "Texas Rangers",
    "Toronto Blue Jays", "Washington Nationals"
  ],
  nhl: [
    "Anaheim Ducks", "Arizona Coyotes", "Boston Bruins", "Buffalo Sabres",
    "Calgary Flames", "Carolina Hurricanes", "Chicago Blackhawks", "Colorado Avalanche",
    "Columbus Blue Jackets", "Dallas Stars", "Detroit Red Wings", "Edmonton Oilers",
    "Florida Panthers", "Los Angeles Kings", "Minnesota Wild", "Montreal Canadiens",
    "Nashville Predators", "New Jersey Devils", "New York Islanders", "New York Rangers",
    "Ottawa Senators", "Philadelphia Flyers", "Pittsburgh Penguins", "San Jose Sharks",
    "Seattle Kraken", "St. Louis Blues", "Tampa Bay Lightning", "Toronto Maple Leafs",
    "Vancouver Canucks", "Vegas Golden Knights", "Washington Capitals", "Winnipeg Jets"
  ],
  mls: [
    "Atlanta United", "Austin FC", "Charlotte FC", "Chicago Fire", "FC Cincinnati",
    "Colorado Rapids", "Columbus Crew", "D.C. United", "FC Dallas", "Houston Dynamo",
    "Inter Miami", "LA Galaxy", "LAFC", "Minnesota United", "CF Montreal",
    "Nashville SC", "New England Revolution", "New York City FC", "New York Red Bulls",
    "Orlando City", "Philadelphia Union", "Portland Timbers", "Real Salt Lake",
    "San Jose Earthquakes", "Seattle Sounders", "Sporting Kansas City", "St. Louis City",
    "Toronto FC", "Vancouver Whitecaps"
  ],
  soccer: [
    "Real Madrid", "Barcelona", "Atlético Madrid", "Manchester United", "Manchester City",
    "Liverpool", "Chelsea", "Arsenal", "Tottenham Hotspur", "Bayern Munich",
    "Borussia Dortmund", "Paris Saint-Germain", "Juventus", "AC Milan", "Inter Milan",
    "Napoli", "Ajax", "Benfica", "Porto", "Celtic", "Rangers",
    "US Men's National Team", "US Women's National Team", "Mexico National Team",
    "England National Team", "Argentina National Team", "Brazil National Team",
    "France National Team", "Germany National Team", "Spain National Team",
    "FIFA", "UEFA", "Premier League", "La Liga", "Serie A", "Bundesliga", "Ligue 1",
    "Champions League", "World Cup", "Other Soccer Team"
  ],
  college: [
    "Alabama Crimson Tide", "Ohio State Buckeyes", "Georgia Bulldogs", "Michigan Wolverines",
    "Texas Longhorns", "USC Trojans", "Notre Dame Fighting Irish", "Clemson Tigers",
    "LSU Tigers", "Florida Gators", "Penn State Nittany Lions", "Oklahoma Sooners",
    "Oregon Ducks", "Tennessee Volunteers", "Auburn Tigers", "Texas A&M Aggies",
    "Miami Hurricanes", "Florida State Seminoles", "Wisconsin Badgers", "UCLA Bruins",
    "Other College Team"
  ],
  nascar: [
    "Kyle Larson", "Chase Elliott", "Denny Hamlin", "Martin Truex Jr.", "Ross Chastain",
    "William Byron", "Christopher Bell", "Ryan Blaney", "Tyler Reddick", "Kevin Harvick",
    "Joey Logano", "Kyle Busch", "Alex Bowman", "Bubba Wallace", "Daniel Suárez",
    "Austin Cindric", "Brad Keselowski", "Chris Buescher", "Aric Almirola", "Austin Dillon",
    "NASCAR Officials", "Other NASCAR Driver"
  ],
  golf: [
    "Scottie Scheffler", "Rory McIlroy", "Jon Rahm", "Patrick Cantlay", "Xander Schauffele",
    "Viktor Hovland", "Max Homa", "Jordan Spieth", "Justin Thomas", "Collin Morikawa",
    "Brooks Koepka", "Bryson DeChambeau", "Dustin Johnson", "Tiger Woods", "Phil Mickelson",
    "Cameron Smith", "Hideki Matsuyama", "Tony Finau", "Rickie Fowler", "Shane Lowry",
    "PGA Tour", "LIV Golf", "The Masters", "US Open", "The Open Championship", "PGA Championship",
    "Other Golfer"
  ],
  other: [
    "Other Team / League"
  ]
};

// Helper to get category from team
const getCategoryFromTeam = (team) => {
  for (const [category, teams] of Object.entries(SPORTS_TEAMS)) {
    if (teams.includes(team)) return category;
  }
  return "other";
};

// ========== Header Component ==========
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b-4 border-brand-blue">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="The Fan Rant Line" 
            className="h-12 w-auto"
            data-testid="header-logo"
          />
        </div>
        <nav className="hidden md:flex items-center gap-6">
          <a href="#rant" className="font-anton text-lg uppercase hover:text-brand-blue transition-colors" data-testid="nav-rant">
            RANT NOW
          </a>
          <a href="#wall" className="font-anton text-lg uppercase hover:text-brand-blue transition-colors" data-testid="nav-wall">
            RANT WALL
          </a>
          <a href="#social" className="font-anton text-lg uppercase hover:text-brand-blue transition-colors" data-testid="nav-social">
            FOLLOW US
          </a>
        </nav>
        <a 
          href="#rant"
          className="btn-brutal bg-brand-blue text-white font-anton text-sm uppercase px-4 py-2 border-2 border-white shadow-brutal-sm hover:shadow-brutal"
          data-testid="header-cta"
        >
          LET IT OUT
        </a>
      </div>
    </header>
  );
};

// ========== Hero Section ==========
const Hero = ({ rantCount }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden noise-overlay">
      {/* Background with halftone pattern */}
      <div className="absolute inset-0 halftone opacity-30" />
      
      {/* Burst accent */}
      <div className="absolute -right-40 top-1/4 w-96 h-96 bg-brand-blue/20 rounded-full blur-3xl" />
      <div className="absolute -left-20 bottom-1/4 w-64 h-64 bg-brand-blue/10 rounded-full blur-2xl" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-12 items-center">
        {/* Text Content */}
        <div className="space-y-8">
          <div className="skew-1">
            <h1 className="font-anton text-6xl md:text-8xl uppercase leading-none tracking-tight" data-testid="hero-title">
              <span className="text-white">THE</span>
              <br />
              <span className="text-brand-blue">FAN RANT</span>
              <br />
              <span className="text-white">LINE</span>
            </h1>
          </div>
          
          <div className="border-l-4 border-brand-blue pl-6 skew-2">
            <p className="font-chivo text-xl md:text-2xl text-gray-300 font-bold" data-testid="hero-tagline">
              Your Voice in Sports Commentary —
              <br />
              <span className="text-brand-blue">Who's Catching Heat Tonight?</span>
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <a 
              href="#rant"
              className="btn-brutal bg-brand-blue text-white font-anton text-2xl uppercase px-8 py-4 border-4 border-white shadow-brutal hover:shadow-brutal-hover"
              data-testid="hero-cta"
            >
              <span className="flex items-center gap-3">
                <Mic size={28} />
                START RANTING
              </span>
            </a>
            <div className="font-mono text-sm text-gray-400 border-2 border-white/20 px-4 py-3 bg-black/50">
              <span className="text-brand-blue font-bold">{rantCount}</span> RANTS SUBMITTED
            </div>
          </div>
        </div>
        
        {/* Logo/Image */}
        <div className="relative flex justify-center lg:justify-end">
          <div className="relative">
            <div className="absolute inset-0 bg-brand-blue/30 blur-3xl rounded-full" />
            <img 
              src={LOGO_URL} 
              alt="The Fan Rant Line" 
              className="relative z-10 w-full max-w-md drop-shadow-2xl"
              data-testid="hero-logo"
            />
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-brand-blue" />
      </div>
    </section>
  );
};

// ========== Rant Form Section ==========
const RantForm = ({ onRantSubmitted }) => {
  const [mode, setMode] = useState("text"); // text or voice
  const [username, setUsername] = useState("");
  const [category, setCategory] = useState("");
  const [team, setTeam] = useState("");
  const [content, setContent] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Get teams for selected category
  const availableTeams = category ? SPORTS_TEAMS[category] || [] : [];

  // Reset team when category changes
  useEffect(() => {
    setTeam("");
  }, [category]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Find supported MIME type
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        ''
      ];
      
      let selectedMimeType = '';
      for (const type of mimeTypes) {
        if (type === '' || MediaRecorder.isTypeSupported(type)) {
          selectedMimeType = type;
          break;
        }
      }
      
      const options = selectedMimeType ? { mimeType: selectedMimeType } : undefined;
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          alert("No audio was recorded. Please try again.");
          return;
        }
        const blob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType || 'audio/webm' 
        });
        console.log("Audio blob created:", blob.size, "bytes, type:", blob.type);
        setAudioBlob(blob);
        setAudioDuration(recordingTime);
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording - request data every second
      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert("Could not access microphone. Please check your browser permissions and try again.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Request any remaining data before stopping
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.requestData();
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Submit text rant
  const submitTextRant = async () => {
    if (!username.trim() || !team || !content.trim()) {
      alert("Please fill in all fields!");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/rants/text`, {
        username: username.trim(),
        team,
        content: content.trim()
      });
      
      setUsername("");
      setCategory("");
      setTeam("");
      setContent("");
      onRantSubmitted();
    } catch (err) {
      console.error("Error submitting rant:", err);
      alert("Failed to submit rant. Please try again.");
    }
    setIsSubmitting(false);
  };

  // Submit voice rant
  const submitVoiceRant = async () => {
    if (!username.trim() || !team || !audioBlob) {
      alert("Please fill in your name, select a team, and record your rant!");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        await axios.post(`${API}/rants/voice`, {
          username: username.trim(),
          team,
          audio_data: base64Audio,
          duration: audioDuration
        });
        
        setUsername("");
        setCategory("");
        setTeam("");
        setAudioBlob(null);
        setAudioDuration(0);
        onRantSubmitted();
        setIsSubmitting(false);
      };
    } catch (err) {
      console.error("Error submitting voice rant:", err);
      alert("Failed to submit rant. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <section id="rant" className="py-20 bg-brand-surface noise-overlay relative">
      <div className="absolute inset-0 halftone opacity-20" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-anton text-5xl md:text-7xl uppercase text-white mb-4" data-testid="rant-section-title">
            DROP YOUR <span className="text-brand-blue">RANT</span>
          </h2>
          <p className="font-chivo text-xl text-gray-400">
            Text it or say it — let the world hear your sports frustration!
          </p>
        </div>
        
        {/* Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex border-4 border-white bg-black">
            <button
              onClick={() => setMode("text")}
              className={`font-anton text-xl uppercase px-8 py-4 transition-all ${
                mode === "text" 
                  ? "bg-brand-blue text-white" 
                  : "bg-transparent text-white hover:bg-white/10"
              }`}
              data-testid="mode-text-btn"
            >
              <span className="flex items-center gap-2">
                <Send size={20} />
                TEXT RANT
              </span>
            </button>
            <button
              onClick={() => setMode("voice")}
              className={`font-anton text-xl uppercase px-8 py-4 transition-all ${
                mode === "voice" 
                  ? "bg-brand-blue text-white" 
                  : "bg-transparent text-white hover:bg-white/10"
              }`}
              data-testid="mode-voice-btn"
            >
              <span className="flex items-center gap-2">
                <Mic size={20} />
                VOICE RANT
              </span>
            </button>
          </div>
        </div>
        
        {/* Form */}
        <div className="bg-brand-card border-4 border-white/30 p-8 shadow-brutal">
          {/* Common Fields */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="font-anton text-lg uppercase text-gray-300 mb-2 block">
                YOUR NAME
              </label>
              <div className="relative">
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your name..."
                  className="w-full bg-black border-4 border-white/30 focus:border-brand-blue text-white text-xl px-12 py-4 font-chivo placeholder:text-gray-600"
                  data-testid="input-username"
                />
              </div>
            </div>
            
            <div>
              <label className="font-anton text-lg uppercase text-gray-300 mb-2 block">
                PICK YOUR LEAGUE
              </label>
              <div className="relative">
                <Trophy size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-black border-4 border-white/30 focus:border-brand-blue text-white text-xl px-12 py-4 font-chivo appearance-none cursor-pointer"
                  data-testid="select-category"
                >
                  <option value="">Select league...</option>
                  {SPORTS_CATEGORIES.filter(c => c.id !== 'all').map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
                <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
            
            <div>
              <label className="font-anton text-lg uppercase text-gray-300 mb-2 block">
                WHO'S CATCHING HEAT?
              </label>
              <div className="relative">
                <Flame size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <select
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  disabled={!category}
                  className="w-full bg-black border-4 border-white/30 focus:border-brand-blue text-white text-xl px-12 py-4 font-chivo appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="select-team"
                >
                  <option value="">{category ? "Select team..." : "Pick league first..."}</option>
                  {availableTeams.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>
          
          {/* Text Mode */}
          {mode === "text" && (
            <div className="mb-6">
              <label className="font-anton text-lg uppercase text-gray-300 mb-2 block">
                YOUR RANT
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Let it all out... Why is your team driving you crazy?!"
                rows={5}
                maxLength={500}
                className="w-full bg-black border-4 border-white/30 focus:border-brand-blue text-white text-xl px-6 py-4 font-chivo placeholder:text-gray-600 resize-none"
                data-testid="textarea-rant"
              />
              <div className="text-right font-mono text-sm text-gray-500 mt-2">
                {content.length}/500
              </div>
            </div>
          )}
          
          {/* Voice Mode */}
          {mode === "voice" && (
            <div className="mb-6">
              <div className="bg-black border-4 border-white/30 p-8 flex flex-col items-center justify-center min-h-[200px]">
                {!isRecording && !audioBlob && (
                  <button
                    onClick={startRecording}
                    className="btn-brutal bg-red-600 text-white font-anton text-2xl uppercase px-12 py-6 border-4 border-white shadow-brutal hover:shadow-brutal-hover flex items-center gap-4"
                    data-testid="btn-start-recording"
                  >
                    <Mic size={32} />
                    START RECORDING
                  </button>
                )}
                
                {isRecording && (
                  <div className="text-center">
                    <div className="relative inline-block mb-6">
                      <div className="w-32 h-32 rounded-full bg-red-600 recording-pulse flex items-center justify-center">
                        <Mic size={48} className="text-white" />
                      </div>
                    </div>
                    <div className="font-anton text-4xl text-red-500 on-air-glow mb-4" data-testid="recording-indicator">
                      ON AIR
                    </div>
                    <div className="font-mono text-3xl text-white mb-6">
                      {formatTime(recordingTime)}
                    </div>
                    <button
                      onClick={stopRecording}
                      className="btn-brutal bg-white text-black font-anton text-xl uppercase px-8 py-4 border-4 border-brand-blue shadow-brutal hover:shadow-brutal-hover flex items-center gap-3 mx-auto"
                      data-testid="btn-stop-recording"
                    >
                      <Square size={24} />
                      STOP
                    </button>
                  </div>
                )}
                
                {audioBlob && !isRecording && (
                  <div className="text-center w-full">
                    <div className="font-anton text-2xl text-brand-blue mb-4">
                      RANT RECORDED!
                    </div>
                    <div className="font-mono text-xl text-gray-400 mb-4">
                      Duration: {formatTime(audioDuration)}
                    </div>
                    <div className="font-mono text-xs text-gray-600 mb-4">
                      Size: {(audioBlob.size / 1024).toFixed(1)} KB
                    </div>
                    <audio 
                      controls 
                      className="w-full max-w-md mx-auto mb-6"
                      data-testid="audio-preview"
                    >
                      <source src={URL.createObjectURL(audioBlob)} type={audioBlob.type} />
                      Your browser does not support audio playback.
                    </audio>
                    <button
                      onClick={() => {
                        setAudioBlob(null);
                        setAudioDuration(0);
                      }}
                      className="font-chivo text-gray-400 hover:text-white underline"
                      data-testid="btn-re-record"
                    >
                      Re-record
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Submit Button */}
          <div className="space-y-4">
            {/* Disclaimer */}
            <p className="font-mono text-xs text-gray-500 text-center px-4">
              By submitting your rant, you agree to our{' '}
              <a href="/terms" className="text-brand-blue hover:underline">Terms of Service</a>.
              The Fan Rant Line may use, share, and distribute your submission across our website, 
              social media, and other media formats.
            </p>
            
            <button
              onClick={mode === "text" ? submitTextRant : submitVoiceRant}
              disabled={isSubmitting}
              className="btn-brutal w-full bg-brand-blue text-white font-anton text-2xl uppercase py-5 border-4 border-white shadow-brutal hover:shadow-brutal-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              data-testid="btn-submit-rant"
            >
              {isSubmitting ? (
                "SUBMITTING..."
              ) : (
                <>
                  <Flame size={28} />
                  SUBMIT YOUR RANT
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ========== Rant of the Day Component ==========
const RantOfTheDay = ({ rant }) => {
  const [likes, setLikes] = useState(rant?.likes || 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const audioRef = useRef(null);
  
  const handleLike = async () => {
    if (!rant) return;
    try {
      const res = await axios.post(`${API}/rants/${rant.id}/like`);
      setLikes(res.data.likes);
    } catch (err) {
      console.error("Error liking rant:", err);
    }
  };
  
  const handleDownload = async () => {
    if (!rant) return;
    setIsDownloading(true);
    try {
      await generateRantImage(rant);
    } catch (err) {
      console.error("Error generating image:", err);
    }
    setIsDownloading(false);
  };
  
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  if (!rant) return null;
  
  return (
    <section className="py-16 bg-gradient-to-b from-brand-black to-brand-surface relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 halftone opacity-10" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-blue/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-yellow-500/10 rounded-full blur-2xl" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-yellow-500 text-black px-6 py-2 mb-4">
            <Crown size={24} />
            <span className="font-anton text-xl uppercase">RANT OF THE DAY</span>
            <Crown size={24} />
          </div>
          <p className="font-chivo text-gray-400">The hottest take that's catching all the heat!</p>
        </div>
        
        {/* Featured Rant Card */}
        <div className="relative">
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 via-brand-blue to-yellow-500 opacity-50 blur-lg" />
          
          <div className="relative bg-brand-card border-4 border-yellow-500 p-8 shadow-[8px_8px_0px_0px_#eab308]" data-testid="rant-of-the-day">
            {/* Star badge */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center rotate-12">
              <Star size={32} className="text-black fill-black" />
            </div>
            
            {/* User info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-brand-blue rounded-full flex items-center justify-center border-4 border-white">
                <User size={32} className="text-white" />
              </div>
              <div>
                <div className="font-anton text-2xl text-white uppercase" data-testid="featured-username">
                  {rant.username}
                </div>
                <div className="font-chivo text-brand-blue font-bold flex items-center gap-2">
                  <Zap size={16} />
                  vs {rant.team}
                </div>
              </div>
            </div>
            
            {/* Content */}
            {rant.rant_type === "text" ? (
              <div className="bg-black/50 border-2 border-white/20 p-6 mb-6">
                <p className="font-chivo text-2xl text-white leading-relaxed" data-testid="featured-content">
                  "{rant.content}"
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <audio 
                  ref={audioRef} 
                  src={`${BACKEND_URL}${rant.audio_url}`}
                  onEnded={() => setIsPlaying(false)}
                  className="hidden"
                />
                <button
                  onClick={toggleAudio}
                  className="w-full bg-black border-4 border-brand-blue p-6 flex items-center gap-6 hover:bg-brand-blue/10 transition-colors"
                  data-testid="featured-play"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isPlaying ? 'bg-brand-blue recording-pulse' : 'bg-white'}`}>
                    {isPlaying ? (
                      <Pause size={32} className="text-white" />
                    ) : (
                      <Play size={32} className="text-black ml-1" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-anton text-xl text-white mb-2">LISTEN TO THIS RANT</div>
                    <div className="flex items-center gap-2">
                      {[...Array(8)].map((_, i) => (
                        <div 
                          key={i}
                          className={`w-2 bg-brand-blue ${isPlaying ? 'wave-bar' : 'h-6'}`}
                          style={{ animationDelay: `${i * 0.08}s` }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="font-mono text-xl text-gray-400">
                    {Math.floor(rant.duration || 0)}s
                  </div>
                </button>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={handleLike}
                  className="btn-brutal flex items-center gap-3 bg-red-600 text-white font-anton text-xl px-6 py-3 border-2 border-white shadow-brutal-sm hover:shadow-brutal"
                  data-testid="featured-like"
                >
                  <Heart size={24} className="fill-white" />
                  <span>{likes} LIKES</span>
                </button>
                
                <button 
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="btn-brutal flex items-center gap-3 bg-brand-blue text-white font-anton text-xl px-6 py-3 border-2 border-white shadow-brutal-sm hover:shadow-brutal disabled:opacity-50"
                  data-testid="featured-download"
                >
                  <Download size={24} className={isDownloading ? 'animate-bounce' : ''} />
                  <span>{isDownloading ? 'CREATING...' : 'SHARE'}</span>
                </button>
              </div>
              
              <div className="font-mono text-sm text-gray-500 flex items-center gap-2">
                <Clock size={16} />
                {new Date(rant.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ========== Rant Card Component ==========
const RantCard = ({ rant, index, isAdmin, onFeatureRant }) => {
  const [likes, setLikes] = useState(rant.likes);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentUsername, setCommentUsername] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const audioRef = useRef(null);
  
  // Fetch comments when expanded
  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/rants/${rant.id}/comments`);
      setComments(res.data);
      setCommentCount(res.data.length);
    } catch (err) {
      console.error("Error fetching comments:", err);
    }
  };
  
  const toggleComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(!showComments);
  };
  
  const submitComment = async () => {
    if (!commentUsername.trim() || !newComment.trim()) {
      alert("Please enter your name and comment!");
      return;
    }
    
    setIsSubmittingComment(true);
    try {
      const res = await axios.post(`${API}/rants/${rant.id}/comments`, {
        username: commentUsername.trim(),
        content: newComment.trim()
      });
      setComments([...comments, res.data]);
      setCommentCount(commentCount + 1);
      setNewComment("");
    } catch (err) {
      console.error("Error submitting comment:", err);
      alert("Failed to submit comment. Please try again.");
    }
    setIsSubmittingComment(false);
  };
  
  const handleLike = async () => {
    try {
      const res = await axios.post(`${API}/rants/${rant.id}/like`);
      setLikes(res.data.likes);
    } catch (err) {
      console.error("Error liking rant:", err);
    }
  };
  
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await generateRantImage(rant);
    } catch (err) {
      console.error("Error generating image:", err);
      alert("Failed to generate image. Please try again.");
    }
    setIsDownloading(false);
  };
  
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const skewClass = index % 3 === 0 ? "skew-1" : index % 3 === 1 ? "skew-2" : "";
  const spanClass = index % 5 === 0 ? "md:col-span-2" : "";
  
  return (
    <div 
      className={`card-brutal bg-brand-card border-2 border-white/30 p-6 ${skewClass} ${spanClass} hover:border-brand-blue transition-colors`}
      data-testid={`rant-card-${rant.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-anton text-xl text-white uppercase" data-testid={`rant-username-${rant.id}`}>
            {rant.username}
          </div>
          <div className="font-chivo text-sm text-brand-blue font-bold">
            vs {rant.team}
          </div>
        </div>
        <div className="font-mono text-xs text-gray-500 flex items-center gap-1">
          <Clock size={12} />
          {formatDate(rant.created_at)}
        </div>
      </div>
      
      {/* Content */}
      {rant.rant_type === "text" ? (
        <p className="font-chivo text-lg text-gray-200 mb-4 leading-relaxed" data-testid={`rant-content-${rant.id}`}>
          "{rant.content}"
        </p>
      ) : (
        <div className="mb-4">
          <audio 
            ref={audioRef} 
            src={`${BACKEND_URL}${rant.audio_url}`}
            onEnded={handleAudioEnded}
            className="hidden"
          />
          <button
            onClick={toggleAudio}
            className="w-full bg-black border-2 border-brand-blue p-4 flex items-center gap-4 hover:bg-brand-blue/10 transition-colors"
            data-testid={`rant-play-${rant.id}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPlaying ? 'bg-brand-blue' : 'bg-white'}`}>
              {isPlaying ? (
                <Pause size={24} className="text-white" />
              ) : (
                <Play size={24} className="text-black ml-1" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <div 
                    key={i}
                    className={`w-1 bg-brand-blue ${isPlaying ? 'wave-bar' : 'h-4'}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="font-mono text-sm text-gray-400">
              {Math.floor(rant.duration || 0)}s
            </div>
          </button>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-4 pt-4 border-t border-white/10">
        <button 
          onClick={handleLike}
          className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors font-chivo"
          data-testid={`rant-like-${rant.id}`}
        >
          <Heart size={18} />
          <span>{likes}</span>
        </button>
        <button 
          onClick={toggleComments}
          className="flex items-center gap-2 text-gray-400 hover:text-brand-blue transition-colors font-chivo"
          data-testid={`rant-comments-${rant.id}`}
        >
          <MessageCircle size={18} />
          <span>{commentCount > 0 ? commentCount : "Reply"}</span>
          {showComments ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button 
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-2 text-gray-400 hover:text-brand-blue transition-colors font-chivo disabled:opacity-50"
          data-testid={`rant-download-${rant.id}`}
        >
          <Download size={18} className={isDownloading ? 'animate-bounce' : ''} />
          <span>{isDownloading ? 'Creating...' : 'Share'}</span>
        </button>
        
        {/* Admin Feature Button */}
        {isAdmin && (
          <button 
            onClick={() => onFeatureRant(rant.id)}
            className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 transition-colors font-chivo ml-auto"
            data-testid={`rant-feature-${rant.id}`}
          >
            <Star size={18} />
            <span>Feature</span>
          </button>
        )}
      </div>
      
      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-white/10">
          {/* Existing Comments */}
          {comments.length > 0 && (
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-black/50 border border-white/10 p-3 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-anton text-sm text-brand-blue">{comment.username}</span>
                    <span className="font-mono text-xs text-gray-600">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="font-chivo text-sm text-gray-300">{comment.content}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Add Comment Form */}
          <div className="space-y-2">
            <input
              type="text"
              value={commentUsername}
              onChange={(e) => setCommentUsername(e.target.value)}
              placeholder="Your name..."
              className="w-full bg-black border-2 border-white/20 focus:border-brand-blue text-white text-sm px-3 py-2 font-chivo placeholder:text-gray-600"
              data-testid={`comment-username-${rant.id}`}
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a reply..."
                className="flex-1 bg-black border-2 border-white/20 focus:border-brand-blue text-white text-sm px-3 py-2 font-chivo placeholder:text-gray-600"
                onKeyPress={(e) => e.key === 'Enter' && submitComment()}
                data-testid={`comment-input-${rant.id}`}
              />
              <button
                onClick={submitComment}
                disabled={isSubmittingComment}
                className="bg-brand-blue text-white px-4 py-2 font-anton text-sm hover:bg-brand-blueHover disabled:opacity-50"
                data-testid={`comment-submit-${rant.id}`}
              >
                {isSubmittingComment ? "..." : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Rant Wall Section ==========
const RantWall = ({ rants, loading, isAdmin, onFeatureRant }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  
  // Filter rants by category
  const filteredRants = activeFilter === "all" 
    ? rants 
    : rants.filter(rant => getCategoryFromTeam(rant.team) === activeFilter);
  
  return (
    <section id="wall" className="py-20 bg-brand-black noise-overlay relative">
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-anton text-5xl md:text-7xl uppercase text-white mb-4" data-testid="wall-section-title">
            THE <span className="text-brand-blue">RANT WALL</span>
          </h2>
          <p className="font-chivo text-xl text-gray-400">
            See what other fans are fired up about
          </p>
        </div>
        
        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="category-filters">
          {SPORTS_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`btn-brutal font-anton text-sm uppercase px-4 py-2 border-2 transition-all ${
                activeFilter === cat.id
                  ? "bg-brand-blue text-white border-white shadow-brutal-sm"
                  : "bg-transparent text-gray-400 border-white/30 hover:border-white hover:text-white"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              <span className="mr-2">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>
        
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-16 h-16 border-4 border-brand-blue border-t-transparent rounded-full animate-spin" />
            <p className="font-chivo text-gray-400 mt-4">Loading rants...</p>
          </div>
        ) : filteredRants.length === 0 ? (
          <div className="text-center py-20 border-4 border-dashed border-white/20">
            <Mic size={64} className="text-gray-600 mx-auto mb-4" />
            <p className="font-anton text-2xl text-gray-500">
              {activeFilter === "all" ? "NO RANTS YET" : `NO ${SPORTS_CATEGORIES.find(c => c.id === activeFilter)?.name} RANTS YET`}
            </p>
            <p className="font-chivo text-gray-600 mt-2">Be the first to let it out!</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <span className="font-mono text-sm text-gray-500">
                Showing {filteredRants.length} {activeFilter !== "all" ? SPORTS_CATEGORIES.find(c => c.id === activeFilter)?.name : ""} rant{filteredRants.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rant-wall-grid">
              {filteredRants.map((rant, index) => (
                <RantCard 
                  key={rant.id} 
                  rant={rant} 
                  index={index} 
                  isAdmin={isAdmin}
                  onFeatureRant={onFeatureRant}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ========== Social Section ==========
const SocialSection = () => {
  const socials = [
    { 
      name: "X / Twitter", 
      icon: Twitter, 
      color: "hover:bg-[#000000]",
      url: "https://x.com/TheFanRantLine",
      handle: "@TheFanRantLine"
    },
    { 
      name: "Instagram", 
      icon: Instagram, 
      color: "hover:bg-gradient-to-r hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737]",
      url: "https://www.instagram.com/thefanrantline/",
      handle: "@thefanrantline"
    },
    { 
      name: "TikTok", 
      icon: ({ size, className }) => (
        <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      ),
      color: "hover:bg-[#000000]",
      url: "https://www.tiktok.com/@user5680552148294",
      handle: "@thefanrantline"
    }
  ];
  
  return (
    <section id="social" className="py-20 bg-brand-surface noise-overlay relative">
      <div className="absolute inset-0 halftone opacity-20" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
        <h2 className="font-anton text-5xl md:text-7xl uppercase text-white mb-4" data-testid="social-section-title">
          JOIN THE <span className="text-brand-blue">CONVERSATION</span>
        </h2>
        <p className="font-chivo text-xl text-gray-400 mb-12">
          Follow us for more sports commentary and fan rants
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {socials.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`btn-brutal bg-black border-4 border-white p-8 shadow-brutal hover:shadow-brutal-hover group transition-all ${social.color}`}
              data-testid={`social-${social.name.toLowerCase()}`}
            >
              <social.icon size={48} className="mx-auto mb-4 text-white group-hover:scale-110 transition-transform" />
              <div className="font-anton text-2xl uppercase text-white mb-2">
                {social.name}
              </div>
              <div className="font-mono text-sm text-gray-400 group-hover:text-white">
                {social.handle}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// ========== Footer ==========
const Footer = () => {
  return (
    <footer className="bg-black border-t-4 border-brand-blue py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo */}
          <div className="text-center md:text-left">
            <img 
              src={LOGO_URL} 
              alt="The Fan Rant Line" 
              className="h-16 mx-auto md:mx-0"
              data-testid="footer-logo"
            />
          </div>
          
          {/* Hotline */}
          <div className="text-center">
            <div className="inline-block border-4 border-brand-blue p-6 bg-brand-surface">
              <div className="font-mono text-sm text-gray-400 mb-2">HOTLINE</div>
              <div className="font-anton text-3xl text-brand-blue" data-testid="footer-hotline">
                1-800-FAN-RANT
              </div>
              <div className="font-chivo text-sm text-gray-500 mt-2">
                (Coming Soon)
              </div>
            </div>
          </div>
          
          {/* Links & Copyright */}
          <div className="text-center md:text-right">
            <div className="mb-4">
              <a 
                href="/terms" 
                className="font-chivo text-gray-400 hover:text-brand-blue transition-colors underline"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </a>
            </div>
            <p className="font-chivo text-gray-500">
              © {new Date().getFullYear()} The Fan Rant Line
            </p>
            <p className="font-chivo text-gray-600 text-sm mt-1">
              Your Voice in Sports Commentary
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ========== Home Page ==========
const HomePage = () => {
  const [rants, setRants] = useState([]);
  const [rantCount, setRantCount] = useState(0);
  const [featuredRant, setFeaturedRant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  
  const fetchRants = useCallback(async () => {
    try {
      const [rantsRes, countRes, featuredRes] = await Promise.all([
        axios.get(`${API}/rants`),
        axios.get(`${API}/rants/count`),
        axios.get(`${API}/rants/featured`)
      ]);
      setRants(rantsRes.data);
      setRantCount(countRes.data.count);
      setFeaturedRant(featuredRes.data);
    } catch (err) {
      console.error("Error fetching rants:", err);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchRants();
    
    // Secret key combo to open admin prompt: Ctrl+Shift+A
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdminPrompt(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fetchRants]);
  
  const handleAdminLogin = async () => {
    if (adminPassword === "October1982") {
      setIsAdmin(true);
      setShowAdminPrompt(false);
      setAdminPassword("");
      alert("Admin mode enabled! You can now feature rants.");
    } else {
      alert("Invalid password!");
    }
  };
  
  const handleFeatureRant = async (rantId) => {
    try {
      await axios.post(`${API}/admin/feature-rant`, {
        password: "October1982",
        rant_id: rantId
      });
      alert("Rant featured successfully!");
      fetchRants(); // Refresh to show new featured rant
    } catch (err) {
      console.error("Error featuring rant:", err);
      alert("Failed to feature rant.");
    }
  };
  
  const handleRantSubmitted = () => {
    fetchRants();
    // Scroll to rant wall
    document.getElementById('wall')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <>
      <Header />
      <Hero rantCount={rantCount} />
      <RantForm onRantSubmitted={handleRantSubmitted} />
      {featuredRant && <RantOfTheDay rant={featuredRant} />}
      <RantWall rants={rants} loading={loading} isAdmin={isAdmin} onFeatureRant={handleFeatureRant} />
      <SocialSection />
      <Footer />
      
      {/* Admin Login Prompt */}
      {showAdminPrompt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-brand-card border-4 border-brand-blue p-8 max-w-md w-full mx-4">
            <h3 className="font-anton text-2xl text-white mb-4">ADMIN LOGIN</h3>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Enter password..."
              className="w-full bg-black border-2 border-white/30 focus:border-brand-blue text-white px-4 py-3 mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
              autoFocus
            />
            <div className="flex gap-4">
              <button
                onClick={handleAdminLogin}
                className="flex-1 bg-brand-blue text-white font-anton py-3 hover:bg-brand-blueHover"
              >
                LOGIN
              </button>
              <button
                onClick={() => {
                  setShowAdminPrompt(false);
                  setAdminPassword("");
                }}
                className="flex-1 bg-gray-700 text-white font-anton py-3 hover:bg-gray-600"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Admin Mode Indicator */}
      {isAdmin && (
        <div className="fixed bottom-4 right-4 bg-brand-blue text-white font-anton px-4 py-2 z-50">
          ADMIN MODE
          <button 
            onClick={() => setIsAdmin(false)} 
            className="ml-3 text-white/70 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

// ========== Main App ==========
function App() {
  return (
    <div className="App min-h-screen bg-brand-black" data-testid="app-container">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
